/* =========================================================
   RareTCG × Timesact — presentation bridge only
   ---------------------------------------------------------
   Timesact owns storefront functionality, modal state and form behaviour.
   RareTCG only labels/arranges the app's PDP release controls so our CSS can
   style them.

   Release-PDP recommendation isolation:
   Timesact's app embed can treat unrelated Dawn quick-add controls as extra
   controls for the current release product. On release PDPs only, normal
   recommendation cards are therefore converted to RareTCG-owned controls
   that do not expose Dawn/Timesact form selectors. Genuine release cards
   remain app-owned.

   Performance rule: observe only the active release buybox and its bounded
   related-products root. No documentElement-wide mutation observer.
========================================================= */
(() => {
  'use strict';
  if (window.__rtcgTimesactPresentationBridge) return;
  window.__rtcgTimesactPresentationBridge = true;

  const normalize = (value) => String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();

  const controlText = (el) => {
    if (!(el instanceof Element)) return '';
    if (el instanceof HTMLInputElement) return normalize(el.value);
    return normalize(el.textContent);
  };

  const classifyControl = (el) => {
    const text = controlText(el);
    if (!text || text.length > 100) return '';
    if (/\bcoming\s*soon\b/.test(text)) return 'coming-soon';
    if (/\bnotify\b/.test(text)) return 'notify';
    if (/\bpre[\s-]?order\b/.test(text) && !/notify/.test(text)) return 'preorder';
    return '';
  };

  const nearestBranch = (el, root) => {
    if (!(el instanceof Element) || !(root instanceof Element)) return null;
    let node = el;
    while (node.parentElement && node.parentElement !== root) node = node.parentElement;
    return node.parentElement === root ? node : null;
  };

  const commonAncestor = (elements, boundary) => {
    if (!elements.length) return null;
    let node = elements[0];
    while (node && node !== boundary) {
      if (elements.every((el) => node.contains(el))) return node;
      node = node.parentElement;
    }
    return null;
  };

  const markMessages = (buybox) => {
    buybox.querySelectorAll('p, small, span, div').forEach((el) => {
      if (!(el instanceof Element) || el.children.length > 3) return;
      if (el.querySelector('button, [role="button"], input[type="button"], input[type="submit"]')) return;
      const text = normalize(el.textContent);
      if (!text || text.length > 160) return;

      if (/^(expected|estimated)?\s*release(?:\s+date)?\s*:\s*$/.test(text)) {
        el.setAttribute('data-rtcg-timesact-message', 'empty-release');
        return;
      }

      if (/be the first to know/.test(text) || /we['’]?.*(?:email|let you know)/.test(text) || /pre[- ]?orders? open/.test(text)) {
        el.setAttribute('data-rtcg-timesact-message', 'supporting');
      }
    });
  };

  const arrangeReleasePdp = (page) => {
    if (!(page instanceof Element) || page.getAttribute('data-rtcg-release-product') !== 'true') return;
    const buybox = page.querySelector('.rtcg-product-buybox');
    if (!buybox) return;

    const candidates = Array.from(buybox.querySelectorAll(
      'button, input[type="button"], input[type="submit"], a[role="button"], [role="button"]'
    ));
    const controls = [];

    candidates.forEach((el) => {
      const kind = classifyControl(el);
      if (!kind) return;
      if (el.closest('[data-rtcg-shipping-open], [data-rtcg-wishlist-bridge]')) return;
      el.setAttribute('data-rtcg-timesact-button', kind);
      controls.push(el);
    });

    markMessages(buybox);
    if (!controls.length) return;

    let group = controls.length > 1 ? commonAncestor(controls, buybox) : null;
    if (!group || group === buybox) {
      const appHosts = controls.map((el) => el.closest('.rtcg-buybox-app')).filter(Boolean);
      if (appHosts.length && appHosts.every((host) => host === appHosts[0])) group = appHosts[0];
    }
    if ((!group || group === buybox) && controls.length === 1) group = controls[0].parentElement;
    if (!group || group === buybox) return;

    group.setAttribute('data-rtcg-timesact-group', 'true');
    controls.forEach((el) => {
      const branch = nearestBranch(el, group);
      if (branch) branch.setAttribute('data-rtcg-timesact-branch', 'true');
    });

    const releaseLine = buybox.querySelector('[data-rtcg-release-date-ui]');
    if (releaseLine && group !== releaseLine && !releaseLine.closest('[data-rtcg-timesact-group="true"]')) {
      const coming = controls.find((el) => el.getAttribute('data-rtcg-timesact-button') === 'coming-soon');
      const preorder = controls.find((el) => el.getAttribute('data-rtcg-timesact-button') === 'preorder');
      const anchorControl = coming || preorder;
      const anchorBranch = anchorControl ? nearestBranch(anchorControl, group) : null;
      if (anchorBranch && anchorBranch.nextSibling) group.insertBefore(releaseLine, anchorBranch.nextSibling);
      else if (anchorBranch) group.appendChild(releaseLine);
      else group.prepend(releaseLine);
    }

    buybox.querySelectorAll('[data-rtcg-timesact-message="supporting"]').forEach((message) => {
      const branch = nearestBranch(message, group);
      if (branch) branch.setAttribute('data-rtcg-timesact-branch', 'true');
    });
  };

  const RELATED_STYLE_ID = 'rtcg-timesact-related-isolation-style';
  const RELATED_ROOT_SELECTOR = '.rtcg-product-related-wrap';
  const RELATED_CARD_SELECTOR = '.rtcg-card:not([data-rtcg-release-state])';

  const ensureRelatedStyles = () => {
    if (document.getElementById(RELATED_STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = RELATED_STYLE_ID;
    style.textContent = `
      ${RELATED_ROOT_SELECTOR} .rtcg-related-safe-quick-add {
        position: relative;
        z-index: 3;
        flex: 0 0 auto;
        width: 100%;
        margin: auto 0 var(--rtcg-card-content-pad-bottom, 13px);
        padding: 0 var(--rtcg-card-content-pad-x, 15px);
      }
      ${RELATED_ROOT_SELECTOR} .rtcg-related-safe-quick-add__control {
        box-sizing: border-box;
        width: 100%;
        max-width: none;
        min-width: 0;
        min-height: var(--rtcg-card-button-height, 48px);
        height: var(--rtcg-card-button-height, 48px);
        margin: 0;
        padding: 0 13px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: 1px solid var(--rtcg-action-primary-border, #f0d27a);
        border-radius: 9px;
        color: var(--rtcg-card-cta-text, #211706);
        background: var(--rtcg-card-cta-bg, linear-gradient(180deg, #fff1a8 0%, #e2c15d 48%, #bd8f31 100%));
        box-shadow: inset 0 1px 0 rgba(255,255,255,.36), 0 4px 12px rgba(119,82,15,.16);
        font-family: inherit;
        font-size: var(--rtcg-card-button-font-size, 12px);
        font-weight: 900;
        letter-spacing: .065em;
        line-height: 1;
        text-align: center;
        text-decoration: none;
        text-transform: uppercase;
        cursor: pointer;
        transition: transform .16s ease, filter .16s ease, box-shadow .16s ease;
        -webkit-tap-highlight-color: transparent;
      }
      ${RELATED_ROOT_SELECTOR} .rtcg-related-safe-quick-add__control:hover:not([disabled]),
      ${RELATED_ROOT_SELECTOR} .rtcg-related-safe-quick-add__control:focus-visible:not([disabled]) {
        color: var(--rtcg-card-cta-text, #211706);
        filter: brightness(1.045) saturate(1.03);
        transform: translateY(-1px);
        box-shadow: inset 0 1px 0 rgba(255,255,255,.42), 0 7px 17px rgba(119,82,15,.22);
        outline: none;
      }
      ${RELATED_ROOT_SELECTOR} .rtcg-related-safe-quick-add__control[disabled] {
        color: var(--rtcg-card-muted, rgba(255,255,255,.62));
        border-color: var(--rtcg-card-border, rgba(216,183,90,.30));
        background: var(--rtcg-card-bg-soft, #000);
        box-shadow: none;
        opacity: .72;
        cursor: not-allowed;
        filter: none;
        transform: none;
      }
      ${RELATED_ROOT_SELECTOR} .rtcg-related-safe-quick-add__control[aria-busy="true"] {
        cursor: wait;
        opacity: .82;
      }
      @media screen and (max-width: 749px) {
        ${RELATED_ROOT_SELECTOR} .rtcg-related-safe-quick-add__control {
          padding-inline: 7px;
          letter-spacing: .045em;
        }
      }
    `;
    document.head.appendChild(style);
  };

  const cardProductUrl = (card) => {
    const link = card.querySelector('.card__heading a[href], a.full-unstyled-link[href]');
    return link?.href || '';
  };

  const rememberNativeRelatedAction = (card, quickAdd) => {
    if (card.dataset.rtcgNativeRelatedAction) return;

    const variantInput = quickAdd?.querySelector('input[name="id"]');
    const optionControl = quickAdd?.querySelector('[data-product-url]');
    const soldOutControl = quickAdd?.querySelector('button[disabled], input[disabled]');
    const productUrl = optionControl?.getAttribute('data-product-url') || cardProductUrl(card);

    if (variantInput?.value && !variantInput.disabled) {
      card.dataset.rtcgNativeRelatedAction = 'add';
      card.dataset.rtcgNativeRelatedVariant = variantInput.value;
      if (productUrl) card.dataset.rtcgNativeRelatedUrl = productUrl;
      return;
    }

    if (optionControl && productUrl && !optionControl.hasAttribute('disabled')) {
      card.dataset.rtcgNativeRelatedAction = 'options';
      card.dataset.rtcgNativeRelatedUrl = productUrl;
      return;
    }

    if (soldOutControl) {
      card.dataset.rtcgNativeRelatedAction = 'soldout';
      return;
    }

    if (productUrl) {
      card.dataset.rtcgNativeRelatedAction = 'product';
      card.dataset.rtcgNativeRelatedUrl = productUrl;
    }
  };

  const buildSafeRelatedControl = (card) => {
    const wrapper = document.createElement('div');
    wrapper.className = 'rtcg-related-safe-quick-add';
    wrapper.setAttribute('data-rtcg-related-safe-quick-add', '');

    const action = card.dataset.rtcgNativeRelatedAction;
    const productUrl = card.dataset.rtcgNativeRelatedUrl || cardProductUrl(card);

    if (action === 'add' && card.dataset.rtcgNativeRelatedVariant) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'rtcg-related-safe-quick-add__control';
      button.dataset.rtcgRelatedAdd = card.dataset.rtcgNativeRelatedVariant;
      button.innerHTML = '<span>Add to cart</span>';
      wrapper.appendChild(button);
      return wrapper;
    }

    if ((action === 'options' || action === 'product') && productUrl) {
      const link = document.createElement('a');
      link.className = 'rtcg-related-safe-quick-add__control';
      link.href = productUrl;
      link.innerHTML = `<span>${action === 'options' ? 'Choose options' : 'View product'}</span>`;
      wrapper.appendChild(link);
      return wrapper;
    }

    const disabled = document.createElement('button');
    disabled.type = 'button';
    disabled.className = 'rtcg-related-safe-quick-add__control';
    disabled.disabled = true;
    disabled.innerHTML = '<span>Sold out</span>';
    wrapper.appendChild(disabled);
    return wrapper;
  };

  const clearRelatedTimesactArtifacts = (card) => {
    card.querySelectorAll(
      '[class*="timesact" i], [id*="timesact" i], [data-timesact-badge]'
    ).forEach((node) => {
      if (!node.closest('[data-rtcg-related-safe-quick-add]')) node.remove();
    });
  };

  const isolateRelatedCard = (card) => {
    if (!(card instanceof Element) || card.hasAttribute('data-rtcg-release-state')) return;

    const quickAdd = card.querySelector('.quick-add');
    if (quickAdd) {
      rememberNativeRelatedAction(card, quickAdd);
      const replacement = buildSafeRelatedControl(card);
      quickAdd.replaceWith(replacement);
      card.setAttribute('data-rtcg-timesact-isolated', 'true');
    } else if (!card.querySelector('[data-rtcg-related-safe-quick-add]')) {
      const content = card.querySelector(':scope > .card > .card__content');
      if (content && card.dataset.rtcgNativeRelatedAction) {
        content.appendChild(buildSafeRelatedControl(card));
        card.setAttribute('data-rtcg-timesact-isolated', 'true');
      }
    }

    clearRelatedTimesactArtifacts(card);
  };

  const scanRelatedCards = (root) => {
    if (!(root instanceof Element)) return;
    root.querySelectorAll(RELATED_CARD_SELECTOR).forEach(isolateRelatedCard);
  };

  const routeConfig = () => {
    if (typeof routes !== 'undefined') return routes;
    return window.routes || {};
  };

  const publishCartEvent = async (eventName, payload) => {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined' || !eventName) return;
    const result = publish(eventName, payload);
    if (result && typeof result.then === 'function') await result;
  };

  const addRelatedVariant = async (button) => {
    if (!(button instanceof HTMLButtonElement) || button.disabled || button.getAttribute('aria-busy') === 'true') return;
    const variantId = button.dataset.rtcgRelatedAdd;
    if (!variantId) return;

    const label = button.querySelector('span');
    const originalLabel = label?.textContent || 'Add to cart';
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const config = routeConfig();

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (label) label.textContent = 'Adding…';

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      if (cart?.getSectionsToRender) {
        const sectionIds = cart.getSectionsToRender().map((section) => section.id);
        formData.append('sections', sectionIds);
        formData.append('sections_url', window.location.pathname);
        cart.setActiveElement?.(button);
      }

      const response = await fetch(config.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });
      const payload = await response.json();

      if (!response.ok || payload?.status) {
        if (typeof PUB_SUB_EVENTS !== 'undefined') {
          await publishCartEvent(PUB_SUB_EVENTS.cartError, {
            source: 'rtcg-related-quick-add',
            productVariantId: variantId,
            errors: payload?.errors || payload?.description,
            message: payload?.message
          });
        }
        throw new Error(payload?.description || payload?.message || 'Unable to add this item.');
      }

      if (typeof PUB_SUB_EVENTS !== 'undefined') {
        await publishCartEvent(PUB_SUB_EVENTS.cartUpdate, {
          source: 'rtcg-related-quick-add',
          productVariantId: variantId,
          cartData: payload
        });
      }

      if (cart?.renderContents) cart.renderContents(payload);
      else window.location.assign(config.cart_url || '/cart');
    } catch (error) {
      console.error('[RareTCG] Related quick add failed:', error);
      if (label) label.textContent = 'Try again';
      window.setTimeout(() => {
        if (label && button.isConnected) label.textContent = originalLabel;
      }, 1800);
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      if (label && label.textContent === 'Adding…') label.textContent = originalLabel;
    }
  };

  let active = null;
  let relatedActive = null;

  const unbindRelatedIsolation = () => {
    relatedActive?.observer?.disconnect();
    if (relatedActive?.root && relatedActive?.clickHandler) {
      relatedActive.root.removeEventListener('click', relatedActive.clickHandler);
    }
    relatedActive = null;
  };

  const bindRelatedIsolation = () => {
    const root = document.querySelector(RELATED_ROOT_SELECTOR);
    if (!root) return;

    ensureRelatedStyles();

    if (relatedActive?.root === root) {
      scanRelatedCards(root);
      return;
    }

    unbindRelatedIsolation();

    let queued = false;
    const queueScan = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        scanRelatedCards(root);
      });
    };

    const clickHandler = (event) => {
      const button = event.target.closest?.('[data-rtcg-related-add]');
      if (!button || !root.contains(button)) return;
      event.preventDefault();
      event.stopPropagation();
      addRelatedVariant(button);
    };

    root.addEventListener('click', clickHandler);
    const observer = new MutationObserver(queueScan);
    observer.observe(root, { childList: true, subtree: true });

    relatedActive = { root, observer, clickHandler };
    scanRelatedCards(root);
  };

  const bindPage = (page) => {
    if (!(page instanceof Element) || page.getAttribute('data-rtcg-release-product') !== 'true') return;
    const buybox = page.querySelector('.rtcg-product-buybox');
    if (!buybox) return;

    bindRelatedIsolation();

    if (active?.buybox === buybox) {
      arrangeReleasePdp(page);
      return;
    }

    active?.observer?.disconnect();
    let queued = false;
    const refresh = () => arrangeReleasePdp(page);
    const queueRefresh = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        refresh();
      });
    };

    const observer = new MutationObserver(queueRefresh);
    observer.observe(buybox, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['value']
    });

    active = { page, buybox, observer };
    refresh();
  };

  const findPage = (root = document) => {
    if (root?.matches?.('.rtcg-product-page[data-rtcg-release-product="true"]')) return root;
    return root?.querySelector?.('.rtcg-product-page[data-rtcg-release-product="true"]') || null;
  };

  const init = () => {
    const page = findPage(document);
    if (page) bindPage(page);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  document.addEventListener('product-info:loaded', (event) => {
    const page = findPage(event.target);
    if (page) bindPage(page);
  });

  document.addEventListener('shopify:section:load', (event) => {
    const page = findPage(event.target) || findPage(document);
    if (page) bindPage(page);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (relatedActive?.root && event.target?.contains?.(relatedActive.root)) unbindRelatedIsolation();
    if (!active?.page || !event.target?.contains?.(active.page)) return;
    active.observer.disconnect();
    active = null;
  });
})();
